// frontend/src/components/Financeiro/FinanceiroDespesas.js
// =============================================
// GESTÃO DE DESPESAS
// ✅ Listagem com filtros (ano/mês/categoria/estado)
// ✅ Paginação local (18 itens/página)
// ✅ Criação e edição de despesas manuais
// ✅ PERSONALIZAÇÃO DE MOEDA E FORMATAÇÃO DINÂMICA
// 🔥 configuracao (moeda+formatacao) + empresa vêm diretamente
//    da resposta do endpoint /api/financeiro/despesas — sem chamadas
//    duplicadas a /api/empresas/minha/atual
// 🔥 REMOVIDO: suporte a despesas de Stock (não são consideradas despesas)
// =============================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Icon } from './SharedComponents';

const API = '/api' || 'http://localhost:3000';
const ITEMS_PER_PAGE = 18;

const CATEGORIAS = [
    'Serviços', 'Renda', 'Água', 'Energia',
    'Internet', 'Telefone', 'Salários', 'Combustível',
    'Manutenção', 'Marketing', 'Impostos', 'Outros'
];

const STATUS_CORES = {
    'Pendente': 'warning',
    'Pago': 'success',
    'Cancelado': 'muted',
    'Vencido': 'danger'
};

const MESES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const ANOS_DISPONIVEIS = (() => {
    const anoAtual = new Date().getFullYear();
    const anos = [];
    for (let i = 0; i < 5; i++) {
        anos.push(anoAtual - i);
    }
    return anos;
})();

// 🔥 CONFIGURAÇÃO PADRÃO (fallback)
const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.',
        posicaoSimbolo: 'depois',
        nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

export default function FinanceiroDespesas() {
    const [despesas, setDespesas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtros, setFiltros] = useState({
        categoria: '',
        status: '',
        ano: new Date().getFullYear(),
        mes: new Date().getMonth()
    });
    const [pagina, setPagina] = useState(1);

    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [despesaEditando, setDespesaEditando] = useState(null);
    const [formData, setFormData] = useState({
        descricao: '',
        nomeDespesa: '',
        categoria: 'Mercadorias',
        nuit: '',
        dataEmissao: new Date().toISOString().split('T')[0],
        total: '',
        status: 'Pendente'
    });
    const [salvando, setSalvando] = useState(false);
    const [erroModal, setErroModal] = useState('');

    // 🔥 Config da empresa — começa com padrão
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({
        Authorization: `Bearer ${token}`
    }), [token]);

    const {
        categoria: filtroCategoria,
        status: filtroStatus,
        ano: filtroAno,
        mes: filtroMes
    } = filtros;

    // =============================================
    // 🔥 FORMATAR MOEDA (DINÂMICA)
    // =============================================
    const formatarMoeda = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda || CONFIG_PADRAO.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais ?? 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    // =============================================
    // 🔥 FORMATAR DATA (DINÂMICA)
    // =============================================
    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig.formatacao?.data || CONFIG_PADRAO.formatacao.data;
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mesNum = String(d.getMonth() + 1).padStart(2, '0');
        const anoNum = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mesNum);
        formato = formato.replace('YYYY', anoNum);
        formato = formato.replace('YY', String(anoNum).slice(-2));

        if (cfg.incluirHora) {
            const horas = String(d.getHours()).padStart(2, '0');
            const minutos = String(d.getMinutes()).padStart(2, '0');
            return `${formato} ${horas}:${minutos}`;
        }

        return formato;
    }, [empresaConfig.formatacao]);

    // =============================================
    // 🔥 APLICAR CONFIGURAÇÃO DA EMPRESA (helper)
    // =============================================
    const aplicarConfiguracao = useCallback((resData) => {
        if (!resData) return;
        const cfg = resData.configuracao;
        const emp = resData.empresa;

        if (cfg || emp) {
            setEmpresaConfig(prev => ({
                ...prev,
                moeda: cfg?.moeda || emp?.moeda || prev.moeda,
                formatacao: cfg?.formatacao || emp?.formatacao || prev.formatacao
            }));
        }
    }, []);

    // =============================================
    // 🔥 ORDENAR DESPESAS — DECRESCENTE POR DATA
    // =============================================
    const ordenarDespesas = useCallback((lista) => {
        return [...lista].sort((a, b) => {
            const dataA = new Date(a.dataEmissao || a.criadoEm || a.createdAt || 0).getTime();
            const dataB = new Date(b.dataEmissao || b.criadoEm || b.createdAt || 0).getTime();

            if (dataB !== dataA) return dataB - dataA;

            const criadoA = new Date(a.criadoEm || a.createdAt || 0).getTime();
            const criadoB = new Date(b.criadoEm || b.createdAt || 0).getTime();
            if (criadoB !== criadoA) return criadoB - criadoA;

            const idA = String(a._id || '');
            const idB = String(b._id || '');
            return idB.localeCompare(idA);
        });
    }, []);

    // =============================================
    // 🔥 RECARREGAR DESPESAS (SÓ DO BACKEND)
    // =============================================
    const recarregarDespesas = useCallback(async () => {
        try {
            const despesasRes = await axios.get(`${API}/financeiro/despesas`, {
                params: {
                    categoria: filtroCategoria || undefined,
                    status: filtroStatus || undefined,
                    ano: filtroAno || undefined,
                    mes: (filtroMes !== null && filtroMes !== undefined) ? filtroMes : undefined,
                    page: 1,
                    limit: 500
                },
                headers
            });

            // 🔥 Aplicar config da resposta
            aplicarConfiguracao(despesasRes.data);

            // 🔥 Todas as entradas são despesas (sem distinção de origem Stock)
            const lista = despesasRes.data?.despesas || [];

            const ordenadas = ordenarDespesas(lista);
            setDespesas(ordenadas);
            return ordenadas;

        } catch (e) {
            console.error('❌ Erro ao recarregar despesas:', e);
            setDespesas([]);
            return [];
        }
    }, [filtroCategoria, filtroStatus, filtroAno, filtroMes, headers, ordenarDespesas, aplicarConfiguracao]);

    // =============================================
    // 🔥 CARREGAR DESPESAS (ao mudar filtros)
    // =============================================
    useEffect(() => {
        let cancelado = false;

        const carregar = async () => {
            setLoading(true);
            try {
                if (!cancelado) {
                    await recarregarDespesas();
                }
            } finally {
                if (!cancelado) setLoading(false);
            }
        };

        carregar();

        return () => { cancelado = true; };
    }, [recarregarDespesas]);

    // =============================================
    // FILTRAGEM LOCAL + ORDENAÇÃO FINAL
    // =============================================
    const despesasFiltradas = useMemo(() => {
        return ordenarDespesas(despesas);
    }, [despesas, ordenarDespesas]);

    // =============================================
    // 🔥 PAGINAÇÃO LOCAL
    // =============================================
    const totalPaginas = Math.max(1, Math.ceil(despesasFiltradas.length / ITEMS_PER_PAGE));

    const despesasPaginadas = useMemo(() => {
        const inicio = (pagina - 1) * ITEMS_PER_PAGE;
        return despesasFiltradas.slice(inicio, inicio + ITEMS_PER_PAGE);
    }, [despesasFiltradas, pagina]);

    const goToPage = (page) => {
        if (page < 1 || page > totalPaginas) return;
        setPagina(page);
    };

    useEffect(() => {
        setPagina(1);
    }, [filtroCategoria, filtroStatus, filtroAno, filtroMes]);

    // =============================================
    // 🔥 RENDER PAGINAÇÃO
    // =============================================
    const renderPagination = () => {
        const page = pagina;
        const pages = totalPaginas;
        const total = despesasFiltradas.length;

        if (pages <= 1) return null;

        const startItem = (page - 1) * ITEMS_PER_PAGE + 1;
        const endItem = Math.min(page * ITEMS_PER_PAGE, total);

        const getPageNumbers = (currentPage, totalPages) => {
            const pagesArray = [];

            if (totalPages <= 5) {
                for (let i = 1; i <= totalPages; i++) {
                    pagesArray.push(i);
                }
                return pagesArray;
            }

            pagesArray.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                startPage = 2;
                endPage = Math.min(totalPages - 1, 4);
            }

            if (currentPage >= totalPages - 2) {
                startPage = Math.max(2, totalPages - 3);
                endPage = totalPages - 1;
            }

            if (startPage > 2) {
                pagesArray.push('...');
            }

            for (let i = startPage; i <= endPage; i++) {
                pagesArray.push(i);
            }

            if (endPage < totalPages - 1) {
                pagesArray.push('...');
            }

            pagesArray.push(totalPages);

            return pagesArray;
        };

        const pageNumbers = getPageNumbers(page, pages);

        return (
            <div className="cotacoes-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} despesas</span>
                </div>
                <div className="pagination-controls">
                    <button
                        className="pagination-btn"
                        onClick={() => goToPage(page - 1)}
                        disabled={page === 1}
                    >
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {pageNumbers.map((p, index) => {
                            if (p === '...') {
                                return (
                                    <span key={`dots-${index}`} className="pagination-dots">
                                        …
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={p}
                                    className={`pagination-number ${page === p ? 'active' : ''}`}
                                    onClick={() => goToPage(p)}
                                >
                                    {p}
                                </button>
                            );
                        })}
                    </div>
                    <button
                        className="pagination-btn"
                        onClick={() => goToPage(page + 1)}
                        disabled={page === pages}
                    >
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // =============================================
    // 🔥 HANDLERS DO MODAL
    // =============================================
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({
            descricao: '',
            nomeDespesa: '',
            categoria: 'Mercadorias',
            nuit: '',
            dataEmissao: new Date().toISOString().split('T')[0],
            total: '',
            status: 'Pendente'
        });
        setErroModal('');
        setDespesaEditando(null);
    };

    const abrirModalCriar = () => {
        setModalMode('create');
        resetForm();
        setShowModal(true);
    };

    const abrirModalEditar = (despesa) => {
        setModalMode('edit');
        setDespesaEditando(despesa);
        setFormData({
            descricao: despesa.descricao || '',
            nomeDespesa: despesa.fornecedor?.nome || '',
            categoria: despesa.categoria || 'Mercadorias',
            nuit: despesa.fornecedor?.nuit || despesa.nuit || '',
            dataEmissao: despesa.dataEmissao
                ? new Date(despesa.dataEmissao).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
            total: despesa.total || despesa.subtotal || '',
            status: despesa.status || 'Pendente'
        });
        setErroModal('');
        setShowModal(true);
    };

    // =============================================
    // 🔥 CRIAR DESPESA
    // =============================================
    const handleCriarDespesa = async (e) => {
        e.preventDefault();
        setErroModal('');
        setSalvando(true);

        try {
            if (!formData.nomeDespesa.trim()) {
                setErroModal('Nome da despesa é obrigatório');
                setSalvando(false);
                return;
            }
            if (!formData.total || parseFloat(formData.total) <= 0) {
                setErroModal('Total deve ser maior que 0');
                setSalvando(false);
                return;
            }

            const total = parseFloat(formData.total) || 0;

            const payload = {
                descricao: formData.descricao.trim() || formData.nomeDespesa.trim(),
                fornecedor: {
                    nome: formData.nomeDespesa.trim(),
                    nuit: formData.nuit.trim() || ''
                },
                categoria: formData.categoria,
                dataEmissao: formData.dataEmissao,
                dataVencimento: null,
                subtotal: total,
                ivaTotal: 0,
                total,
                status: formData.status,
                ivaDedutivel: false,
                origem: 'Manual',
                itens: [{
                    descricao: formData.nomeDespesa.trim(),
                    quantidade: 1,
                    precoUnitario: total,
                    descontoPercentual: 0,
                    subtotal: total,
                    ivaPercent: 0,
                    ivaValor: 0,
                    total
                }]
            };

            const res = await axios.post(`${API}/contabilidade/despesas`, payload, { headers });
            if (res.data.success || res.data.despesa) {
                const numero = res.data.despesa?.numero || '(sem número)';
                console.log(`✅ Despesa criada: ${numero}`);
                setShowModal(false);
                resetForm();
                await recarregarDespesas();
                setPagina(1);
            }
        } catch (err) {
            console.error(err);
            setErroModal(
                err.response?.data?.message ||
                err.response?.data?.erro ||
                'Erro ao criar despesa'
            );
        } finally {
            setSalvando(false);
        }
    };

    // =============================================
    // 🔥 ATUALIZAR DESPESA
    // =============================================
    const handleAtualizarDespesa = async (e) => {
        e.preventDefault();
        setErroModal('');
        setSalvando(true);

        try {
            if (!despesaEditando) return;

            if (!formData.nomeDespesa.trim()) {
                setErroModal('Nome da despesa é obrigatório');
                setSalvando(false);
                return;
            }
            if (!formData.total || parseFloat(formData.total) <= 0) {
                setErroModal('Total deve ser maior que 0');
                setSalvando(false);
                return;
            }

            const total = parseFloat(formData.total) || 0;

            const payload = {
                descricao: formData.descricao.trim() || formData.nomeDespesa.trim(),
                fornecedor: {
                    nome: formData.nomeDespesa.trim(),
                    nuit: formData.nuit.trim() || ''
                },
                categoria: formData.categoria,
                dataEmissao: formData.dataEmissao,
                dataVencimento: null,
                subtotal: total,
                ivaTotal: 0,
                total,
                status: formData.status,
                ivaDedutivel: false,
                itens: [{
                    descricao: formData.nomeDespesa.trim(),
                    quantidade: 1,
                    precoUnitario: total,
                    descontoPercentual: 0,
                    subtotal: total,
                    ivaPercent: 0,
                    ivaValor: 0,
                    total
                }]
            };

            const res = await axios.put(
                `${API}/contabilidade/despesas/${despesaEditando._id}`,
                payload,
                { headers }
            );

            if (res.data.success) {
                setShowModal(false);
                resetForm();
                await recarregarDespesas();
            }
        } catch (err) {
            console.error(err);
            setErroModal(
                err.response?.data?.message ||
                err.response?.data?.erro ||
                'Erro ao atualizar despesa'
            );
        } finally {
            setSalvando(false);
        }
    };

    const limparFiltros = () => {
        setFiltros({
            categoria: '',
            status: '',
            ano: new Date().getFullYear(),
            mes: new Date().getMonth()
        });
        setPagina(1);
    };

    return (
        <div className="fin-despesas">
            <header className="fin-section-header">
                <div className="fin-section-title">
                    <Icon name="receipt_long" size={24} color="var(--fin-primary)" />
                    <div>
                        <h2>Gestão de Despesas</h2>
                        <p>{despesasFiltradas.length} despesas registadas</p>
                    </div>
                </div>
                <div className="fin-section-actions">
                    <select
                        value={filtros.ano}
                        onChange={e => { setFiltros({ ...filtros, ano: parseInt(e.target.value) }); setPagina(1); }}
                        className="fin-filter-select"
                    >
                        {ANOS_DISPONIVEIS.map(a => (
                            <option key={a} value={a}>{a}</option>
                        ))}
                    </select>

                    <select
                        value={filtros.mes}
                        onChange={e => { setFiltros({ ...filtros, mes: e.target.value === '' ? null : parseInt(e.target.value) }); setPagina(1); }}
                        className="fin-filter-select"
                    >
                        <option value="">Todos os meses</option>
                        {MESES.map((m, i) => (
                            <option key={i} value={i}>{m}</option>
                        ))}
                    </select>

                    <select
                        value={filtros.categoria}
                        onChange={e => { setFiltros({ ...filtros, categoria: e.target.value }); setPagina(1); }}
                        className="fin-filter-select"
                    >
                        <option value="">Todas as categorias</option>
                        {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>

                    <select
                        value={filtros.status}
                        onChange={e => { setFiltros({ ...filtros, status: e.target.value }); setPagina(1); }}
                        className="fin-filter-select"
                    >
                        <option value="">Todos os estados</option>
                        <option value="Pendente">Pendente</option>
                        <option value="Pago">Pago</option>
                        <option value="Cancelado">Cancelado</option>
                        <option value="Vencido">Vencido</option>
                    </select>

                    <button className="fin-btn-secondary" onClick={limparFiltros}>
                        <Icon name="refresh" size={14} color="currentColor" /> Limpar
                    </button>

                    <button className="fin-btn-primary" onClick={abrirModalCriar}>
                        <Icon name="add" size={14} color="#fff" /> Nova Despesa
                    </button>
                </div>
            </header>

            {loading ? (
                <div className="fin-loading"><div className="fin-spinner" />A carregar...</div>
            ) : despesasFiltradas.length === 0 ? (
                <div className="fin-empty">
                    <Icon name="receipt_long" size={48} color="var(--fin-muted)" />
                    <h3>Sem despesas</h3>
                    <p>Não há despesas registadas com os filtros atuais.</p>
                </div>
            ) : (
                <>
                    <div className="fin-table-wrapper">
                        <table className="fin-table">
                            <thead>
                                <tr>
                                    <th>Ref</th>
                                    <th>Data</th>
                                    <th>Nome da Despesa</th>
                                    <th>Categoria</th>
                                    <th>Total</th>
                                    <th>Estado</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {despesasPaginadas.map((d) => {
                                    const podeEditar = d.status !== 'Pago';

                                    const nomeDespesa = d.fornecedor?.nome || d.descricao || '—';

                                    const referencia = d.numero
                                        ? d.numero
                                        : (d._id ? `#${String(d._id).slice(-6)}` : '—');

                                    return (
                                        <tr key={d._id}>
                                            <td><strong>{referencia}</strong></td>
                                            <td>{formatarData(d.dataEmissao)}</td>
                                            <td>{nomeDespesa}</td>
                                            <td>
                                                <span className="fin-badge badge-info">{d.categoria}</span>
                                            </td>
                                            <td><strong>{formatarMoeda(d.total)}</strong></td>
                                            <td>
                                                <span className={`fin-badge badge-${STATUS_CORES[d.status] || 'muted'}`}>
                                                    {d.status}
                                                </span>
                                            </td>
                                            <td>
                                                {podeEditar && (
                                                    <button
                                                        className="fin-btn-icon"
                                                        onClick={() => abrirModalEditar(d)}
                                                        title="Editar despesa"
                                                    >
                                                        <Icon name="edit" size={16} color="var(--fin-primary)" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {renderPagination()}
                </>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => !salvando && setShowModal(false)}>
                    <div className="modal-content modal-confirm" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <Icon name="receipt_long" size={20} />
                                {modalMode === 'create' ? ' Nova Despesa' : ' Editar Despesa'}
                            </h3>
                            <button className="modal-close" onClick={() => !salvando && setShowModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            {erroModal && (
                                <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>
                                    <Icon name="error" size={16} />
                                    <span>{erroModal}</span>
                                </div>
                            )}
                            <form onSubmit={modalMode === 'create' ? handleCriarDespesa : handleAtualizarDespesa}>
                                <div className="form-group">
                                    <label>Nome da Despesa <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="nomeDespesa"
                                        value={formData.nomeDespesa}
                                        onChange={handleChange}
                                        placeholder="Ex: Compra de material de escritório"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Descrição</label>
                                    <input
                                        type="text"
                                        name="descricao"
                                        value={formData.descricao}
                                        onChange={handleChange}
                                        placeholder="Descrição adicional (opcional)"
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Categoria <span className="required">*</span></label>
                                        <select name="categoria" value={formData.categoria} onChange={handleChange}>
                                            {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Estado <span className="required">*</span></label>
                                        <select name="status" value={formData.status} onChange={handleChange}>
                                            <option value="Pendente">Pendente</option>
                                            <option value="Pago">Pago</option>
                                            <option value="Cancelado">Cancelado</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>NUIT</label>
                                        <input
                                            type="text"
                                            name="nuit"
                                            value={formData.nuit}
                                            onChange={handleChange}
                                            placeholder="NUIT (opcional)"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Data de Emissão <span className="required">*</span></label>
                                        <input
                                            type="date"
                                            name="dataEmissao"
                                            value={formData.dataEmissao}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Total <span className="required">*</span></label>
                                    <input
                                        type="number"
                                        name="total"
                                        value={formData.total}
                                        onChange={handleChange}
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                        required
                                    />
                                </div>

                                <div className="modal-actions">
                                    <button type="submit" className="modal-btn-primary" disabled={salvando}>
                                        {salvando ? 'A guardar...' : (modalMode === 'create' ? 'Criar Despesa' : 'Guardar Alterações')}
                                    </button>
                                    <button
                                        type="button"
                                        className="modal-btn-secondary"
                                        onClick={() => { setShowModal(false); resetForm(); }}
                                        disabled={salvando}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}